import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, InlineNotification, SearchField, Table, useLocalizeMessage, } from 'libs.nucleus';
import { getAssignedEnvironmentSiteIds } from '../mom_adapter/assignment_join';
import { EnvironmentSelector } from '../site_configuration/environment_selector.component';
const displaySiteValue = (value) => value?.trim() || '—';
/** First study environment that has a checked site. Empty lists and untouched environments do not count. */
const firstEnvironmentWithSelection = (environments, pendingSelectionByEnv) => {
    for (const environment of environments ?? []) {
        const selected = pendingSelectionByEnv?.[environment.id];
        if (selected && selected.length > 0) {
            return environment.id;
        }
    }
    return undefined;
};
const matchesSiteSearch = (site, query) => {
    if (!query) {
        return true;
    }
    const name = (site.name ?? '').toLowerCase();
    const number = (site.number ?? '').toLowerCase();
    const piName = (site.piName ?? '').toLowerCase();
    return name.includes(query) || number.includes(query) || piName.includes(query);
};
/**
 * Writes the environments the editor has changed. Empty `pendingByEnv` is a no-op. Assignment is
 * stored by template key, so this also updates the published version of that template.
 */
export const commitPendingSiteAssignments = async ({ saveAssignments, templateKey, pendingByEnv, sites, }) => {
    for (const [environmentIdText, selectedIds] of Object.entries(pendingByEnv)) {
        const environmentId = Number(environmentIdText);
        const environmentSites = (sites ?? []).filter((site) => site.environmentId === environmentId);
        try {
            await saveAssignments(environmentId, templateKey, selectedIds, environmentSites);
        }
        catch {
            return false;
        }
    }
    return true;
};
export const ConsentTemplateSitePicker = ({ templateKey, environments, sites, hasLoadError = false, onRetryLoad, loadAssignments, pendingSelectionByEnv, onSelectionChange, }) => {
    const translate = useLocalizeMessage();
    const [selectedEnvironmentId, setSelectedEnvironmentId] = useState(() => firstEnvironmentWithSelection(environments, pendingSelectionByEnv));
    const environmentChosenRef = useRef(selectedEnvironmentId !== undefined);
    const [assignments, setAssignments] = useState([]);
    const [search, setSearch] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const editedEnvRef = useRef(new Set());
    useEffect(() => {
        if (environmentChosenRef.current) {
            return;
        }
        const next = firstEnvironmentWithSelection(environments, pendingSelectionByEnv);
        if (next === undefined) {
            return;
        }
        environmentChosenRef.current = true;
        setSelectedEnvironmentId(next);
    }, [environments, pendingSelectionByEnv]);
    const handleEnvironmentChange = useCallback((environmentId) => {
        environmentChosenRef.current = true;
        setSelectedEnvironmentId(environmentId);
    }, []);
    useEffect(() => {
        if (selectedEnvironmentId === undefined) {
            setAssignments([]);
            return;
        }
        let cancelled = false;
        setIsLoading(true);
        setLoadFailed(false);
        void loadAssignments(selectedEnvironmentId)
            .then((next) => {
            if (!cancelled) {
                setAssignments(next);
            }
        })
            .catch(() => {
            if (!cancelled) {
                setLoadFailed(true);
                setAssignments([]);
            }
        })
            .finally(() => {
            if (!cancelled) {
                setIsLoading(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [loadAssignments, selectedEnvironmentId]);
    useEffect(() => {
        if (selectedEnvironmentId === undefined) {
            setSelectedIds([]);
            return;
        }
        if (pendingSelectionByEnv && selectedEnvironmentId in pendingSelectionByEnv) {
            setSelectedIds(pendingSelectionByEnv[selectedEnvironmentId] ?? []);
            return;
        }
        if (editedEnvRef.current.has(selectedEnvironmentId)) {
            return;
        }
        setSelectedIds(getAssignedEnvironmentSiteIds(assignments, templateKey));
    }, [assignments, pendingSelectionByEnv, selectedEnvironmentId, templateKey]);
    const sitesForEnvironment = useMemo(() => {
        if (sites === undefined || selectedEnvironmentId === undefined) {
            return [];
        }
        return sites.filter((site) => site.environmentId === selectedEnvironmentId);
    }, [selectedEnvironmentId, sites]);
    const visibleSites = useMemo(() => {
        const query = search.trim().toLowerCase();
        return sitesForEnvironment.filter((site) => matchesSiteSearch(site, query));
    }, [search, sitesForEnvironment]);
    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const applySelection = useCallback((nextIds) => {
        setSelectedIds(nextIds);
        if (selectedEnvironmentId === undefined) {
            return;
        }
        editedEnvRef.current.add(selectedEnvironmentId);
        onSelectionChange?.(selectedEnvironmentId, nextIds);
    }, [onSelectionChange, selectedEnvironmentId]);
    const toggleSite = useCallback((site, checked) => {
        const nextIds = checked
            ? selectedIds.includes(site.id)
                ? selectedIds
                : [...selectedIds, site.id]
            : selectedIds.filter((id) => id !== site.id);
        applySelection(nextIds);
    }, [applySelection, selectedIds]);
    const allVisibleSelected = visibleSites.length > 0 && visibleSites.every((site) => selectedIdSet.has(site.id));
    const someVisibleSelected = visibleSites.some((site) => selectedIdSet.has(site.id));
    const toggleVisibleSites = useCallback((checked) => {
        const visibleIds = new Set(visibleSites.map((site) => site.id));
        const kept = selectedIds.filter((id) => !visibleIds.has(id));
        const nextIds = checked ? [...kept, ...visibleSites.map((site) => site.id)] : kept;
        applySelection(nextIds);
    }, [applySelection, selectedIds, visibleSites]);
    const columns = useMemo(() => [
        {
            id: 'assigned',
            size: 48,
            header: () => (_jsx(Checkbox, { label: translate('Select all sites'), hideLabel: true, checked: allVisibleSelected, indeterminate: someVisibleSelected && !allVisibleSelected, onChange: toggleVisibleSites, dataTestId: 'template-site-select-all' })),
            cell: (context) => (_jsx(Checkbox, { label: translate('Assign site'), hideLabel: true, checked: selectedIdSet.has(context.row.original.id), onChange: (checked) => toggleSite(context.row.original, checked), dataTestId: `template-site-${context.row.original.id}` })),
        },
        {
            header: translate('Site Number'),
            accessorKey: 'number',
            cell: (context) => displaySiteValue(context.getValue()),
        },
        {
            header: translate('Site Name'),
            accessorKey: 'name',
            cell: (context) => displaySiteValue(context.getValue()),
        },
        {
            header: translate('PI Name'),
            accessorKey: 'piName',
            cell: (context) => displaySiteValue(context.getValue()),
        },
        {
            header: translate('Address'),
            accessorKey: 'addressDisplay',
            cell: (context) => displaySiteValue(context.getValue()),
        },
    ], [allVisibleSelected, selectedIdSet, someVisibleSelected, toggleSite, toggleVisibleSites, translate]);
    if (hasLoadError) {
        return (_jsxs("div", { className: 'flex flex-col items-start gap-3', children: [_jsx(InlineNotification, { type: 'error', title: translate("Couldn't load this study's environments and sites"), subtitle: translate('Site assignments cannot be changed until this loads.') }), _jsx(Button, { label: translate('Try again'), onClick: onRetryLoad, dataTestId: 'template-site-picker-retry' })] }));
    }
    return (_jsxs("section", { className: 'flex w-full flex-col gap-2.5', "data-testid": 'template-site-picker', children: [_jsxs("div", { children: [_jsxs("h3", { className: 'm-0 text-base font-semibold text-neutral-900', children: [translate('Sites'), ' ', _jsxs("span", { className: 'text-xs font-normal tracking-[0.0417em] text-neutral-900/60', children: ["(", translate('Optional'), ")"] })] }), _jsx("p", { className: 'm-0 mt-1 text-sm text-neutral-500', children: translate('Which sites this version is for. You can publish with none selected, but the version reaches nobody until at least one site is added.') })] }), _jsxs("div", { className: 'flex w-full flex-col gap-6', children: [_jsxs("div", { className: 'flex w-full flex-wrap items-end gap-4', children: [_jsx("div", { className: 'w-[384px]', children: _jsx(SearchField, { ariaLabel: translate('Search'), placeholder: translate('Search site number, name or PI'), value: search, onChange: (event) => setSearch(event.target.value), onClear: () => setSearch(''), width: 'full', dataTestId: 'template-site-search' }) }), _jsx(EnvironmentSelector, { environments: environments, selectedEnvironmentId: selectedEnvironmentId, onChange: handleEnvironmentChange, selectTestId: 'template-site-environment' })] }), loadFailed && (_jsx(InlineNotification, { type: 'error', title: translate('Sites could not be loaded'), subtitle: translate('Try again. Nothing was changed.') })), _jsx("div", { className: 'w-full', "data-testid": 'template-site-table', children: _jsx(Table, { columns: columns, data: visibleSites, getRowId: (row) => String(row.id), isLoading: isLoading, hasPagination: true, initialPageSize: 10, paginationNavigationScreenReaderLabel: translate('Sites pages'), labelNoResults: selectedEnvironmentId === undefined
                                ? translate('Select an environment to configure.')
                                : translate('No sites found for this environment.'), getRowCustomClasses: (site) => (selectedIdSet.has(site.id) ? '!bg-[#F6F3FF]' : '') }) })] })] }));
};
